/**
 * [TEAM-SAMEER 2026-10-15, user] NEW FILE — the "Team Sameer Report" tab, right after Lead Management.
 * User: "after lead management tab add a tab for 'Team Sameer Analysis' … it will check seo hubspot raw schema and the leads raw
 * table … there is a column where we have added team sameer … the name will have the name of lead generated by, email will be
 * for them, the columns where the date is written it will show the count of rows they have for the particular date selected,
 * nal will have the count for the columns where they will have the nal keyword or in the nal verification status they have nal
 * accepted tag, out of those rows and then final count will be lead count minus nal rows … it will just have the 2 date thing,
 * one is start date selection and the other will be end date selection", then "make the name 'Team Sameer Report'"; no yellow
 * highlight ("skip that yellow thing").
 *
 * Title left, Start date + End date right (the Lead Management date box; default = the 21st-to-20th cycle of today, as the
 * user's sheet: "21 Sep 2026 - 20 Oct 2026"). Table: Name · Email · <the picked dates> · NAL (red, as the Lead Generator
 * Report) · Final Count, by Final Count high → low, every header sorts, columns resize, a Total line last. The numbers are
 * worked out on the server (GET /api/team-sameer-report — services/teamSameerReport.ts). Nothing signals a Leads_Raw change
 * (the sheet sync every 10 minutes, Transfer to Leads Raw), so the page looks again every minute while on show and when the
 * tab comes back; a background look keeps the table on show (no skeleton, no flash).
 * [TEAM-SAMEER-GROUPS 2026-10-15, user] The same table is divided into ROW / US lead generators and their teams (linesOf).
 * [TEAM-SAMEER-DUP 2026-10-16, user] "after the nal columns, add a column for duplicate … from the leads data section lead status
 * should be duplicate … the number will be in red just like nal and the final count will be leads data minus nal minus
 * duplicate": a red Duplicate column after NAL; Final Count = count − NAL − Duplicate (worked out on the server).
 * [TEAM-SAMEER-SIDE 2026-10-19, user] "on the top on the just left side of the start date i want to add a toggle and it will have
 * US and ROW and when i switch it … the names for that will show … right now it is showing complete names but i want to see the
 * names as per the selected toggle": a US | ROW switch left of the Start date; each side's view = its teams and people and its
 * own Total line (linesOf). The switch is instant (both views come from the one answer) and the tables slide.
 * [TEAM-SAMEER-NAMED 2026-10-19, user] "2 columns one for the 'Named Leads' and the other for the 'No Named Leads' … for that same
 * count [the final count] i want to show bifurcation" + "after the final count column then show these 2 columns": Named Leads ·
 * No Named Leads after Final Count — the Final Count by Leads_Raw's Lead Type, worked out on the server (named + noName = final).
 * [TEAM-SAMEER-GEN 2026-10-27, user] "copy those 4 columns and paste it there before the date column": the Lead Generator Report's
 * Unique (websites) · NAL · Duplicate · Final Count of the same dates, right after Email (server: the report's own code, per person).
 * [TEAM-SAMEER-HEADS] + [TEAM-SAMEER-NOT-ASSIGNED 2026-10-27, user] the headings read as one flow (Lead Generator Name · Lead Generator
 * Email · Unique (Website) · NAL (Before Assignment) · Duplicate (Before Assignment) · Count for Assignment · Not Assigned Yet · <dates>
 * · NAL (After Assignment) · Duplicate (After Assignment) · Final Count · Named Leads · No Named Leads); Not Assigned Yet = the leads
 * of Count for Assignment not in Leads Raw yet (server).
 * [TEAM-SAMEER-ONE-DUP 2026-10-27, user] "why 2 times usage for this" → "make the thing like this": ONE Duplicate column, right after
 * Count for Assignment, and no "Duplicate (After Assignment)" — a duplicate is never assigned, so it comes off once: Unique (Website)
 * − NAL (Before Assignment) = Count for Assignment − Duplicate − Not Assigned Yet = <dates> − NAL (After Assignment) = Final Count
 * (the numbers are worked out on the server).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import DataTable from '../components/DataTable';
import SingleDateCalendar from '../components/SingleDateCalendar';
import PureInk from '../components/PureInk'; // [PURE-INK 2026-10-15]
import { fetchTeamSameerReport, invalidateGetCacheByPrefix, TeamSameerReport, TeamSameerRow } from '../services/api';
import { Column, SortConfig } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useRealtime } from '../hooks/useRealtime'; // [LGP 2026-10-15]
import { SlidePane, slidePos } from '../components/ViewSlide'; // [TEAM-SAMEER-SIDE 2026-10-19]
import { Seg, SideViewport } from './lgp/lgpShared'; // [TEAM-SAMEER-SIDE] Lead Generator Performance's US | ROW switch + slide box
import { leadsNoDock } from '../config/sections'; // [TL-REPORT-ONLY 2026-10-19]

/* ── helpers ── */

const dark = (p: { theme: DefaultTheme }) => p.theme.colorScheme === 'dark';
/** Today in India (Leads_Raw's days are IST days). en-CA writes YYYY-MM-DD. */
const IST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
const todayIst = () => IST_DAY.format(new Date());
/** Leads_Raw's first Assigned Date is 2 Jan 2025. */
const FIRST_DAY = '2025-01-01';
const ymd = (y: number, m0: number, d: number) => new Date(Date.UTC(y, m0, d)).toISOString().slice(0, 10);
/** The 21st-to-20th cycle that holds `today` (the user's sheet: "21 Sep 2026 - 20 Oct 2026" on 15 Oct). */
function cycleOf(today: string): { from: string; to: string } {
  const [y, m, d] = today.split('-').map(Number);
  return d >= 21 ? { from: ymd(y, m - 1, 21), to: ymd(y, m, 20) } : { from: ymd(y, m - 2, 21), to: ymd(y, m - 1, 20) };
}
/** The last day a date box offers: the end of next month (so the whole current cycle / month can be picked). */
const lastDayOffered = (today: string) => { const [y, m] = today.split('-').map(Number); return ymd(y, m + 1, 0); };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-09-21" → "21 Sep 2026". */
const dayLabel = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : d;
};
/** The count column's heading — the picked dates, as the user's sheet writes them. */
const rangeLabel = (from: string, to: string) => (from === to ? dayLabel(from) : `${dayLabel(from)} - ${dayLabel(to)}`);
const errorText = (e: any, fallback: string): string => e?.response?.data?.error || fallback;
/** Leads_Raw changes without a version number (the sheet sync, Transfer to Leads Raw): look again every minute. */
const REFRESH_MS = 60_000;
/** The floating dock's reach (bottom 18 + about 68 tall): the table's own bottom pad lifts the last row above it. */
const DOCK_CLEAR = 88;

/* The app's header sort (the Lead Generator Report's rule): the first click low → high / A → Z, the same header again turns
   it round, a blank is the lowest; equal values keep the server's order (Final Count, high to low). The ROW / US parts, the
   teams and the Total line keep their places (linesOf). */
const NO_SORT: SortConfig = { sortBy: '', sortOrder: 'asc' };
const nextSort = (prev: SortConfig, key: string): SortConfig => ({ sortBy: key, sortOrder: prev.sortBy === key && prev.sortOrder === 'asc' ? 'desc' : 'asc' });
const SORT_TEXT = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const sortKey = (v: unknown): number | string | null => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  return s ? s : null;
};
const compareKeys = (x: number | string | null, y: number | string | null): number => {
  if (x === null || y === null) return x === y ? 0 : x === null ? -1 : 1;
  return typeof x === 'number' && typeof y === 'number' ? x - y : SORT_TEXT.compare(String(x), String(y));
};

/* [TEAM-SAMEER-GROUPS 2026-10-15, user] "in the same table make the division for the row lead generator and us lead generator as
   per the team reporting structure table that we have and also add the division for the teams also like ankit, manjeet etc":
   ONE table, grouped as the Lead Generator Report groups its two tables — the ROW part (people whose ROW Status in the lead
   generators table is ROW): its teams (a team line with its own sums, then its people), then its part line "ROW lead
   generators" with the part's sums; then the same for the US part (everyone else); the Total line last. [TEAM-SAMEER-PART-END
   2026-10-15, user] "the row and us lead total row should show below the table, right now it shows on the top and then the
   table": a part line closes its part, as a table's Total line does. Teams A–Z, then Team Freelancer, then people not in the
   table ("No team"). A header sort keeps the parts and the teams: the people inside each team follow it, the named teams follow
   their team line (by the team's name for Name / Email); ROW stays first, Team Freelancer + No team stay after the named teams,
   each part line closes its part, the Total line stays last.
   [TEAM-SAMEER-SIDE 2026-10-19, user] The parts are now two views behind a US | ROW switch: a view holds ONE side — its teams
   (same order and sort rules), their people, then a Total line of that side ("N lead generators" and the side's sums). The part
   line ("ROW / US lead generators") is gone: alone in its view it would only repeat that Total. */
type Side = 'US' | 'ROW';
const SIDES: readonly Side[] = ['US', 'ROW']; // US first: the switch reads US | ROW and the views slide in that order
const SIDE_OPTIONS: { key: Side; label: string }[] = SIDES.map((key) => ({ key, label: key }));
type LineKind = 'team' | 'total';
/** A table line: a lead generator, or a team line, the Total line (kind). */
interface ReportLine {
  id: string; kind?: LineKind; key: string; name: string | null; email: string;
  leads: number; nal: number; final: number; named: number; noName: number; team: string | null; region: 'ROW' | 'US' | null;
  genUnique: number; genNal: number; genDuplicate: number; genFinal: number; // [TEAM-SAMEER-GEN] the Lead Generator Report's
  notAssigned: number; // [TEAM-SAMEER-NOT-ASSIGNED] of Count for Assignment: not in Leads Raw yet
}
const TEAM_LAST = ['Team Freelancer', 'No team'];
const peopleText = (n: number) => `${n} lead generator${n === 1 ? '' : 's'}`;
const sumsOf = (xs: TeamSameerRow[]) => xs.reduce((s, r) => ({ leads: s.leads + r.leads, nal: s.nal + r.nal, final: s.final + r.final,
  named: s.named + r.named, noName: s.noName + r.noName, // [TEAM-SAMEER-NAMED]
  genUnique: s.genUnique + r.genUnique, genNal: s.genNal + r.genNal, genDuplicate: s.genDuplicate + r.genDuplicate, genFinal: s.genFinal + r.genFinal, // [TEAM-SAMEER-GEN]
  notAssigned: s.notAssigned + r.notAssigned }), // [TEAM-SAMEER-NOT-ASSIGNED]
{ leads: 0, nal: 0, final: 0, named: 0, noName: 0, genUnique: 0, genNal: 0, genDuplicate: 0, genFinal: 0, notAssigned: 0 }); // [TEAM-SAMEER-ONE-DUP] one Duplicate: genDuplicate
const cellOf = (row: unknown, key: string): unknown => (row as Record<string, unknown>)[key];
/** The rows in a sort's order (stable: equal values keep their order; the same array back with no sort). */
function sortedBy<T>(rows: T[], sort: SortConfig): T[] {
  if (!sort.sortBy) return rows;
  const dir = sort.sortOrder === 'desc' ? -1 : 1;
  return rows.map((r, i) => ({ r, i, k: sortKey(cellOf(r, sort.sortBy)) }))
    .sort((a, b) => compareKeys(a.k, b.k) * dir || a.i - b.i)
    .map((x) => x.r);
}
/** [TEAM-SAMEER-SIDE] One side's view: its teams, their people, then the side's Total line ([] = nobody on that side). */
function linesOf(data: TeamSameerReport | null, sort: SortConfig, side: Side): ReportLine[] {
  const members = data ? data.rows.filter((r) => r.region === side) : [];
  if (!members.length) return [];
  const dir = sort.sortOrder === 'desc' ? -1 : 1;
  const rank = (t: string) => TEAM_LAST.indexOf(t) + 1; // 0 = a named team
  // a team line's value for the sort: the team's name for the two name columns, else its own sum
  const teamValue = (line: ReportLine) => sortKey(sort.sortBy === 'name' || sort.sortBy === 'email' ? line.name : cellOf(line, sort.sortBy));
  const out: ReportLine[] = [];
  const byTeam = new Map<string, TeamSameerRow[]>();
  for (const r of members) {
    const t = r.team || 'No team';
    if (!byTeam.has(t)) byTeam.set(t, []);
    byTeam.get(t)!.push(r);
  }
  const teams = [...byTeam].map(([t, list]) => {
    const line: ReportLine = { id: `team:${side}:${t}`, kind: 'team', key: `team:${side}:${t}`, name: t, email: peopleText(list.length), team: t, region: side, ...sumsOf(list) };
    return { t, list, line };
  });
  teams.sort((a, b) => rank(a.t) - rank(b.t) || (sort.sortBy ? compareKeys(teamValue(a.line), teamValue(b.line)) * dir : 0) || a.t.localeCompare(b.t));
  for (const { line, list } of teams) {
    out.push(line);
    for (const r of sortedBy(list, sort)) out.push({ ...r, id: r.key }); // no sort = the server's order: Final Count, high to low
  }
  // the side's Total line closes the view (its people and sums only)
  out.push({ id: 'total', kind: 'total', key: 'total', name: 'Total', email: peopleText(members.length), team: null, region: side, ...sumsOf(members) });
  return out;
}

/* ── layout (Lead Management's: the page fills the card, the top bar stays, only the table scrolls) ── */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 4px 2px 0;
  color: ${(p) => p.theme.text}; /* [PURE-INK 2026-10-15] what has no colour of its own reads the pure ink too */

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the whole page (title, US | ROW, the dates, the
     table) is ONE scroll, as Lead Management ("in the phone view nothing will be fixed on the top side otherwise how will i
     see the data for the below"). The two side views and the table take their full height (TableScroll below). */
  @media (max-width: 768px) {
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;

    & > ${SideViewport} { flex: none; min-height: auto; }
    & > ${SideViewport} > * { height: auto; }
  }
`;

/* The Payments / Lead Management top strip: the title left, the two dates right. */
const TopBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -4px -2px 0;
  padding: 12px 20px;
  background: ${(p) => (dark(p)
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  border-bottom: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px; /* [PHONE-LAYOUT 2026-10-23] was 12 */
    padding: 10px 12px;
  }
`;

const PageTitle = styled.h1`
  height: 38px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [PHONE-LAYOUT 2026-10-23] phones: the shared phone page title (Lead Management's), 17 px heavy, centred */
  @media (max-width: 768px) { height: auto; font-size: 17px; font-weight: 800; text-align: center; }
`;

const Dates = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-end;

  /* [PHONE-LAYOUT 2026-10-23] phones: the US | ROW switch centred on its own line, then the two dates side by side, each
     with its caption above (they were three ragged lines at the right: the switch, Start date, End date) */
  @media (max-width: 768px) {
    align-self: stretch;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    & > [data-seg] { grid-column: 1 / -1; justify-self: center; }
  }
`;

const DateField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .cap { font-size: 12px; font-weight: 600; color: ${(p) => p.theme.muted}; white-space: nowrap; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the caption above the date, the date across its half of the line */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    min-width: 0;
    & > button { width: 100%; }
  }
`;

/* [TEAM-SAMEER-NO-DOCK 2026-10-15, user] "just like i have added this thing that the tab switch pill will not be seen by the shivam
   and harshit, make the same for these 4 also": the Leads type has no dock (AppLayout [LEADS-NO-DOCK] — directory_role 'leads',
   not admin), so for them the table's end runs down to the card's bottom, as Lead Management's page view does ($noDock). */
const TableScroll = styled.div<{ $noDock?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: ${(p) => (p.$noDock ? 16 : DOCK_CLEAR)}px;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  th, td { white-space: nowrap; }
  tbody tr[data-row-index] { cursor: default; }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only swipes
     sideways; the page scrolls (Wrap). The end pad (88 px, 16 for the Leads type) still lifts the last row above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
  }
`;

const Muted = styled.span`
  color: ${(p) => p.theme.muted};
`;

/* The Lead Generator Report's NAL look: red. [PURE-INK 2026-10-15, user] A 0 used to sit at half strength; "no blurry or light
   tone of the colour": a 0 is as clear as any number now. */
const NegNum = styled.span`
  color: ${(p) => p.theme.danger}; /* [PURE-INK] the theme's red: light #c81e1e (5 : 1 on the grey lines), dark #f87171 */
  font-weight: 700;
`;

/* [TEAM-SAMEER-FINAL-GREEN 2026-10-19, user] "make the final count column values in green colour, so that it will be proper
   highlighted": the theme's green text colour (greenInk: dark #4acd8b, light #16735a — readable on the grey team lines and the
   green Total line), bold as before. */
const FinalNum = styled.span`
  color: ${(p) => p.theme.greenInk};
  font-weight: 700;
`;

/* ── the page ── */

const TeamSameerReportPage: React.FC = () => {
  const { user } = useAuth();
  const noDock = leadsNoDock(user); // [TEAM-SAMEER-NO-DOCK] = AppLayout's isLeadsOnly ([TL-REPORT-ONLY] a TL with two tabs has the dock)
  // [TEAM-SAMEER-SIDE 2026-10-19] US | ROW: US the first time, then this browser's last pick (as Lead Generator Performance's switch)
  const sideKey = `dt-team-sameer-side:${user?.id ?? 'anon'}`;
  const [side, setSide] = useState<Side>(() => {
    try { return localStorage.getItem(sideKey) === 'ROW' ? 'ROW' : 'US'; } catch { return 'US'; }
  });
  const pickSide = useCallback((next: Side) => {
    setSide(next);
    try { localStorage.setItem(sideKey, next); } catch { /* storage blocked: the pick holds for this visit */ }
  }, [sideKey]);
  const today = useMemo(() => todayIst(), []);
  const lastDay = useMemo(() => lastDayOffered(today), [today]);
  const [range, setRange] = useState(() => cycleOf(today));
  const { from, to } = range;
  const rangeKey = `${from}|${to}`;
  const [state, setState] = useState<{ key: string; data: TeamSameerReport | null; error: string | null }>({ key: '', data: null, error: null });
  const keyNow = useRef(rangeKey);
  keyNow.current = rangeKey;

  // quiet = a background look: a failure keeps what is on show
  const load = useCallback((quiet: boolean) => {
    const k = rangeKey;
    invalidateGetCacheByPrefix(['/team-sameer-report|']);
    fetchTeamSameerReport(from, to)
      .then((data) => { if (keyNow.current === k) setState({ key: k, data, error: null }); })
      .catch((e) => {
        if (keyNow.current !== k) return;
        setState((s) => (quiet && s.key === k && s.data ? s : { key: k, data: null, error: errorText(e, 'Could not load the report.') }));
      });
  }, [from, to, rangeKey]);

  useEffect(() => { load(false); }, [load]);
  // [LGP 2026-10-15] The roster now lives in our database and Lead Generator Performance edits it: a saved roster change (team,
  // ROW Status) shows here at once.
  useRealtime(['leadGenPerf'], () => load(true));
  useEffect(() => {
    const t = window.setInterval(() => { if (!document.hidden) load(true); }, REFRESH_MS);
    const onVis = () => { if (!document.hidden) load(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  const data = state.key === rangeKey ? state.data : null;
  const failed = state.key === rangeKey && !!state.error;
  const loading = !data && !failed;

  const [sort, setSort] = useState<SortConfig>(NO_SORT);
  const onSort = useCallback((key: string) => setSort((s) => nextSort(s, key)), []);
  // [TEAM-SAMEER-SIDE] both views from the one answer: the switch never reloads, never shows a skeleton
  const linesBySide = useMemo(() => ({ US: linesOf(data, sort, 'US'), ROW: linesOf(data, sort, 'ROW') }), [data, sort]);

  const isDark = (useTheme() as DefaultTheme).colorScheme === 'dark';
  // the Total line in the app's green and a team line lightly tinted, as the Lead Generator Report's ([TEAM-SAMEER-SIDE] the
  // violet / blue part lines went with the US | ROW switch)
  const rowBg = useCallback((row: ReportLine) => {
    if (row.kind === 'total') return isDark ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.10)';
    if (row.kind === 'team') return isDark ? 'rgba(255,255,255,0.055)' : 'rgba(22,25,23,0.05)';
    return undefined;
  }, [isDark]);

  const columns: Column[] = useMemo(() => [
    /* [TEAM-SAMEER-HEADS 2026-10-27, user] "the columns heading will be Lead Generator Name, Lead generator Email, Unique (Website),
       NAL (Before Assignment), Duplicate (Before Assignment), Count for Assignment, Not Assigned Yet, Date thing, NAL (After
       Assignment), Duplicate (After Assignment), Final Count": the flow reads left to right — before the assignment (the Lead
       Generator Report's numbers, [TEAM-SAMEER-GEN]), what still waits ([TEAM-SAMEER-NOT-ASSIGNED]), then Leads Raw after it. */
    { key: 'name', label: 'Lead Generator Name', sortable: true, render: (v: string | null, row: ReportLine) => (row.kind ? <b>{v}</b> : v || <Muted>—</Muted>) },
    { key: 'email', label: 'Lead Generator Email', sortable: true, render: (v: string, row: ReportLine) => (row.kind ? <Muted>{v}</Muted> : v || <Muted>—</Muted>) },
    // [TEAM-SAMEER-GEN 2026-10-27, user] "just copy those 4 columns and paste it there before the date column": the Lead Generator
    // Report's Unique (websites) · NAL · Duplicate · Final Count of the same dates, in its look (a team / Total line's Unique bold,
    // NAL + Duplicate red, Final Count bold)
    { key: 'genUnique', label: 'Unique (Website)', sortable: true, align: 'right', render: (v: number, row: ReportLine) => (row.kind ? <b>{v}</b> : v) },
    { key: 'genNal', label: 'NAL (Before Assignment)', sortable: true, align: 'right', render: (v: number) => <NegNum>{v}</NegNum> },
    { key: 'genFinal', label: 'Count for Assignment', sortable: true, align: 'right', render: (v: number) => <b>{v}</b> }, // [TEAM-SAMEER-ONE-DUP] = Unique − NAL (server)
    // [TEAM-SAMEER-ONE-DUP 2026-10-27, user] the ONE Duplicate, after Count for Assignment (was "Duplicate (Before Assignment)" before
    // it and "Duplicate (After Assignment)" after the dates — the same rows twice): a duplicate is never assigned, so it comes off here
    { key: 'genDuplicate', label: 'Duplicate', sortable: true, align: 'right', render: (v: number) => <NegNum>{v}</NegNum> },
    // [TEAM-SAMEER-NOT-ASSIGNED 2026-10-27, user] of Count for Assignment, the leads not in Leads Raw yet (they wait for Assign to
    // HubSpot — today's leads until the evening run): Count for Assignment − Duplicate − Not Assigned Yet = the date column
    { key: 'notAssigned', label: 'Not Assigned Yet', sortable: true, align: 'right', render: (v: number, row: ReportLine) => (row.kind ? <b>{v}</b> : v) },
    { key: 'leads', label: rangeLabel(from, to), sortable: true, align: 'right', render: (v: number, row: ReportLine) => (row.kind ? <b>{v}</b> : v) },
    { key: 'nal', label: 'NAL (After Assignment)', sortable: true, align: 'right', render: (v: number) => <NegNum>{v}</NegNum> },
    { key: 'final', label: 'Final Count', sortable: true, align: 'right', render: (v: number) => <FinalNum>{v}</FinalNum> }, // [TEAM-SAMEER-FINAL-GREEN] · [TEAM-SAMEER-ONE-DUP] = the dates − NAL (After Assignment)
    // [TEAM-SAMEER-NAMED 2026-10-19] the Final Count by Lead Type, after it (as the count column: a team / Total line in bold)
    { key: 'named', label: 'Named Leads', sortable: true, align: 'right', render: (v: number, row: ReportLine) => (row.kind ? <b>{v}</b> : v) },
    { key: 'noName', label: 'No Named Leads', sortable: true, align: 'right', render: (v: number, row: ReportLine) => (row.kind ? <b>{v}</b> : v) },
  ], [from, to]);

  const pickFrom = useCallback((d: string) => setRange((r) => ({ from: d, to: r.to })), []);
  const pickTo = useCallback((d: string) => setRange((r) => ({ from: r.from, to: d })), []);

  return (
    <Wrap data-team-sameer-report="">
      <TopBar>
        <PageTitle>Team Sameer Report</PageTitle>
        <Dates>
          {/* [TEAM-SAMEER-SIDE 2026-10-19, user] US | ROW, just left of the Start date */}
          <Seg label="Side" value={side} onPick={pickSide} options={SIDE_OPTIONS} />
          <DateField data-date="from">
            <span className="cap">Start date</span>
            <SingleDateCalendar value={from} min={FIRST_DAY} max={to} onChange={pickFrom} hint={false} liquid /* [LIQUID-GLASS 2026-11-02] glass capsule */ />
          </DateField>
          <DateField data-date="to">
            <span className="cap">End date</span>
            <SingleDateCalendar value={to} min={from} max={lastDay} onChange={pickTo} hint={false} liquid />
          </DateField>
        </Dates>
      </TopBar>
      {/* [TEAM-SAMEER-SIDE] the two views side by side, the one on show in place, the other just off to its side
          (components/ViewSlide, quiet + flat: the table only slides — Lead Generator Performance's US | ROW) */}
      <SideViewport>
        {SIDES.map((s) => (
          <SlidePane key={s} $pos={slidePos(side, s, SIDES)} $quiet $flat data-side-pane={s} aria-hidden={side !== s}>
            <TableScroll $noDock={noDock}>
              <DataTable
                columns={columns}
                data={linesBySide[s]}
                sort={sort}
                onSort={onSort}
                loading={loading}
                rowBgFn={rowBg}
                disableMobileCards
                parentScroll
                resizeStorageKey={`dt-team-sameer-report-colw:${user?.id ?? 'anon'}`}
                loadFailed={failed ? { what: 'the Team Sameer Report', onRetry: () => load(false) } : undefined}
                empty={{ title: `No Team Sameer leads for ${s} lead generators ${from === to ? `on ${dayLabel(from)}` : `from ${dayLabel(from)} to ${dayLabel(to)}`}` }}
              />
            </TableScroll>
          </SlidePane>
        ))}
      </SideViewport>
    </Wrap>
  );
};

/** [PURE-INK 2026-10-15, user] "same in the team sameer report tab also": the page under the pure-ink theme (components/PureInk). */
const TeamSameerReportPureInk: React.FC = () => <PureInk><TeamSameerReportPage /></PureInk>;

export default TeamSameerReportPureInk;
